(() => {
  const MOBILE_QUERY = "(max-width: 767px)";
  const ACTIVE_CLASS = "is-mobile-sticky-active";
  const HIDDEN_CLASS = "is-mobile-sticky-hidden";
  const HIGHLIGHT_CLASS = "is-mobile-sticky-target";
  const CONFIRMED_CLASS = "is-size-confirmed";
  const UNCONFIRMED_CLASS = "is-size-unconfirmed";
  const mobileMedia = window.matchMedia(MOBILE_QUERY);

  const selectors = {
    form: "[data-js-product-form]",
    sticky: "[data-mobile-sticky-atc]",
    stickyButton: ".js-sticky-add-to-cart-button",
    stickyText: "[data-mobile-sticky-atc-text]",
    mainButton: ".product-form__submit",
    fixedButtonWrapper: ".product-form__add-to-cart-wrapper",
    sizeFieldset: ".product-options__list-view",
    colorInput: ".product-options input[type='radio'], .product-options select",
    sizeInput: ".product-options__list-view input[type='radio'], .product-options__list-view select",
    zoomModal: "[data-zoom-modal].is-active, .zoom-gallery.is-active, .modal.is-active",
  };

  const getHeaderOffset = () => {
    const header = document.querySelector(".header-section, .shopify-section-header, header");
    return header ? Math.min(header.getBoundingClientRect().height, 90) : 0;
  };

  const getStickyFooterOffset = () => {
    const wrapper = document.querySelector(selectors.fixedButtonWrapper);
    const sticky = document.querySelector(`${selectors.sticky}.${ACTIVE_CLASS}:not(.${HIDDEN_CLASS})`);

    if (sticky) {
      const stickyStyles = window.getComputedStyle(sticky);

      if (stickyStyles.display !== "none" && stickyStyles.visibility !== "hidden") {
        return Math.min(sticky.getBoundingClientRect().height, 120);
      }
    }

    if (wrapper) {
      const styles = window.getComputedStyle(wrapper);

      if (styles.display !== "none" && styles.visibility !== "hidden" && styles.position === "fixed") {
        return Math.min(wrapper.getBoundingClientRect().height, 120);
      }
    }

    return 0;
  };

  const onMediaChange = (callback) => {
    if (typeof mobileMedia.addEventListener === "function") {
      mobileMedia.addEventListener("change", callback);
      return;
    }

    if (typeof mobileMedia.addListener === "function") {
      mobileMedia.addListener(callback);
    }
  };

  const scrollToSize = (sizeFieldset) => {
    if (!sizeFieldset) return;

    const rect = sizeFieldset.getBoundingClientRect();
    const headerOffset = getHeaderOffset();
    const footerOffset = mobileMedia.matches ? getStickyFooterOffset() : 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleTop = headerOffset;
    const visibleBottom = Math.max(viewportHeight - footerOffset, visibleTop + rect.height);
    const visibleCenter = visibleTop + (visibleBottom - visibleTop) / 2;
    const targetTop = rect.top + window.pageYOffset + rect.height / 2 - visibleCenter;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: "smooth",
    });

    sizeFieldset.classList.add(HIGHLIGHT_CLASS);
    window.setTimeout(() => sizeFieldset.classList.remove(HIGHLIGHT_CLASS), 1400);
  };

  const hasSizeOption = (sizeFieldset) => Boolean(sizeFieldset);

  const setSizeConfirmed = (form, sizeFieldset, isConfirmed = false) => {
    const confirmed = !hasSizeOption(sizeFieldset) || isConfirmed;
    form.dataset.mobileStickySizeConfirmed = confirmed ? "true" : "false";

    if (sizeFieldset) {
      sizeFieldset.classList.toggle(UNCONFIRMED_CLASS, mobileMedia.matches && !confirmed);
    }
  };

  const hasConfirmedSize = (form, sizeFieldset) => {
    if (!hasSizeOption(sizeFieldset)) return true;
    if (!mobileMedia.matches) return true;
    return form.dataset.mobileStickySizeConfirmed === "true";
  };

  const setStickyText = (sticky) => {
    const text = sticky.querySelector(selectors.stickyText);
    if (!text) return;

    const addText = sticky.dataset.addToCartText || text.textContent;
    text.textContent = addText;
  };

  const updateState = ({ form, sticky, sizeFieldset, mainButton, isMainVisible }) => {
    if (!sticky) return;

    const isMobile = mobileMedia.matches;
    const isConfirmed = hasConfirmedSize(form, sizeFieldset);
    const isDisabled = mainButton && mainButton.disabled;
    const modalOpen = Boolean(document.querySelector(selectors.zoomModal));

    if (sizeFieldset) {
      sizeFieldset.classList.toggle(UNCONFIRMED_CLASS, isMobile && !isConfirmed);
    }

    sticky.classList.toggle(CONFIRMED_CLASS, isConfirmed);
    sticky.classList.toggle(HIDDEN_CLASS, !isMobile || isMainVisible || modalOpen);
    sticky.classList.toggle(ACTIVE_CLASS, isMobile && !isMainVisible && !modalOpen);
    sticky.setAttribute("aria-hidden", (!isMobile || isMainVisible || modalOpen).toString());

    const stickyButton = sticky.querySelector(selectors.stickyButton);
    if (stickyButton && !isDisabled) {
      stickyButton.disabled = false;
    }

    setStickyText(sticky);
  };

  const initForm = (form) => {
    const sticky = form.querySelector(selectors.sticky);
    const mainButton = form.querySelector(selectors.mainButton);
    const sizeFieldset = form.querySelector(selectors.sizeFieldset);

    if (!mainButton || form.dataset.mobileStickyInitialized === "true") return;

    form.dataset.mobileStickyInitialized = "true";

    if (sticky) {
      sticky.dataset.mobileStickyInitialized = "true";
    }

    setSizeConfirmed(form, sizeFieldset, false);

    const state = {
      form,
      sticky,
      sizeFieldset,
      mainButton,
      isMainVisible: false,
    };

    const refresh = () => updateState(state);

    const syncCurrentSizeFieldset = () => {
      state.sizeFieldset = form.querySelector(selectors.sizeFieldset);
      return state.sizeFieldset;
    };

    if (sticky && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          state.isMainVisible = Boolean(entry && entry.isIntersecting);
          refresh();
        },
        {
          threshold: 0.15,
          rootMargin: "0px 0px -12px 0px",
        }
      );

      observer.observe(mainButton);
    }

    const guardAddToCart = (event) => {
      const currentSizeFieldset = syncCurrentSizeFieldset();
      if (hasConfirmedSize(form, currentSizeFieldset)) return;

      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      scrollToSize(currentSizeFieldset);
      refresh();
    };

    mainButton.addEventListener("click", guardAddToCart, true);
    form.addEventListener("submit", guardAddToCart, true);
    form.addEventListener("product:require-size", () => {
      scrollToSize(syncCurrentSizeFieldset());
      refresh();
    });

    if (sticky) {
      sticky.addEventListener(
        "click",
        (event) => {
          const stickyButton = event.target.closest(selectors.stickyButton);
          if (!stickyButton) return;

          guardAddToCart(event);
        },
        true
      );
    }

    form.addEventListener("change", (event) => {
      const input = event.target;
      if (!input || !input.matches(selectors.colorInput)) return;

      const currentSizeFieldset = syncCurrentSizeFieldset();

      if (input.matches(selectors.sizeInput) || input.closest(selectors.sizeFieldset)) {
        setSizeConfirmed(form, currentSizeFieldset, true);
        refresh();
        return;
      }

      if (hasSizeOption(currentSizeFieldset)) {
        setSizeConfirmed(form, currentSizeFieldset, false);
      }

      window.setTimeout(() => {
        syncCurrentSizeFieldset();
        refresh();
      }, 60);
    });

    onMediaChange(() => {
      syncCurrentSizeFieldset();
      refresh();
    });
    document.addEventListener("cart:updated", refresh);
    document.addEventListener("theme:cart:drawer:open", () => {
      if (sticky) sticky.classList.add(HIDDEN_CLASS);
    });
    document.addEventListener("theme:modal:open", refresh);
    document.addEventListener("theme:modal:close", refresh);

    refresh();
  };

  const init = () => {
    document.querySelectorAll(selectors.form).forEach(initForm);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
